import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { exportArrayToCsv } from '@/lib/csvExport';
import { joApi, joErr } from '@/services/processingJoApi';
import { DateRangeBar } from './DateRangeBar';
import { fmtN } from './JoBits';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
// [key, header, value, tone(row) → class when the figure needs attention]
const COLS = [
  ['orders', 'Orders', (r) => fmtN(r.orders)],
  ['bundles', 'Bundles', (r) => fmtN(r.bundles)],
  ['sent_pcs', 'Sent', (r) => fmtN(r.sent_pcs)],
  ['received_pcs', 'Back', (r) => fmtN(r.received_pcs)],
  ['back_pct', 'Back %', (r) => pct(r.back_pct), (r) => r.back_pct !== null && r.back_pct < 90 && 'text-amber-700 font-semibold'],
  ['out_pcs', 'Still out', (r) => fmtN(r.out_pcs), (r) => r.out_pcs > 0 && 'text-rose-700'],
  ['extra_pcs', 'Extra back', (r) => fmtN(r.extra_pcs), (r) => r.extra_pcs > 0 && 'text-amber-700'],
  ['avg_days_back', 'Avg days to return', (r) => (r.avg_days_back === null ? '—' : r.avg_days_back)],
  ['on_time_pct', 'On time', (r) => (r.due_bundles ? `${pct(r.on_time_pct)} (${r.late_bundles} late)` : '—'), (r) => r.on_time_pct !== null && r.on_time_pct < 80 && 'text-amber-700 font-semibold'],
  ['damage_pct', 'Damage', (r) => `${fmtN(r.damaged_pcs)} · ${pct(r.damage_pct)}`],
  ['reject_pct', 'Rejected', (r) => `${fmtN(r.rejected_pcs)} · ${pct(r.reject_pct)}`, (r) => r.reject_pct > 5 && 'text-rose-700 font-semibold'],
  ['return_checks', 'Return checks', (r) => fmtN(r.return_checks), (r) => r.return_checks > 0 && 'text-amber-700'],
];

const csv = (rows) => exportArrayToCsv(rows, [{ key: 'jobber_name', header: 'Jobber' }, { key: 'orders', header: 'Orders' }, { key: 'consignments', header: 'Consignments' },
  { key: 'bundles', header: 'Bundles' }, { key: 'sent_pcs', header: 'Sent pcs' }, { key: 'received_pcs', header: 'Back pcs' }, { key: 'back_pct', header: 'Back %' },
  { key: 'out_pcs', header: 'Still out pcs' }, { key: 'extra_pcs', header: 'Extra back pcs' }, { key: 'avg_days_back', header: 'Avg days to return' },
  { key: 'due_bundles', header: 'Bundles with a due date' }, { key: 'late_bundles', header: 'Late bundles' }, { key: 'on_time_pct', header: 'On time %' },
  { key: 'damaged_pcs', header: 'Damaged pcs' }, { key: 'damage_pct', header: 'Damage %' }, { key: 'rejected_pcs', header: 'Rejected pcs' },
  { key: 'reject_pct', header: 'Rejected %' }, { key: 'return_checks', header: 'Return check mismatches' }], 'jobber-scorecard.csv');

/** Per jobber over job-order consignments sent in the range: pieces back, speed, lateness, damage / rejection, return mismatches. */
export const JobberScorecard = () => {
  const [range, setRange] = useState({ from: '', to: '' });
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    const t = setTimeout(() => joApi.scorecard({ date_from: range.from || undefined, date_to: range.to || undefined }).then(setData)
      .catch((e) => toast.error(joErr(e, 'Could not load the scorecard'))), 300);
    return () => clearTimeout(t);
  }, [range]);
  return (
    <div className="space-y-3" data-testid="jo-scorecard">
      <div className="flex flex-wrap items-center gap-3">
        <DateRangeBar from={range.from} to={range.to} onChange={setRange} testPrefix="score-date" />
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => csv(data.rows)} disabled={!data?.rows.length} data-testid="score-csv-btn"><Download className="mr-1 h-4 w-4" />CSV</Button>
      </div>
      {data && <p className="text-xs text-gray-500" data-testid="score-range-note">Consignments sent {data.date_from} to {data.date_to} · damage and rejection are % of pieces back
        {data.unassigned_consignments ? ` · ${data.unassigned_consignments} consignment(s) without an assigned unit are left out` : ''}</p>}
      <Card className="border-0 shadow-md"><CardContent className="overflow-x-auto p-0">
        {!data ? <Loader2 className="mx-auto my-12 h-6 w-6 animate-spin text-[#2D5F4F]" /> : (
          <Table data-testid="score-table">
            <TableHeader><TableRow><TableHead>Jobber</TableHead>{COLS.map(([k, h]) => <TableHead key={k} className="whitespace-nowrap text-right">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {!data.rows.length && <TableRow><TableCell colSpan={COLS.length + 1} className="py-10 text-center text-gray-500" data-testid="score-empty">No job-order consignments sent to a jobber in this range</TableCell></TableRow>}
              {data.rows.map((r) => (
                <TableRow key={r.jobber_name} data-testid={`score-row-${slug(r.jobber_name)}`}>
                  <TableCell className="font-medium">{r.jobber_name}</TableCell>
                  {COLS.map(([k, , v, tone]) => <TableCell key={k} className={`whitespace-nowrap text-right tabular-nums ${(tone && tone(r)) || ''}`} data-testid={`score-${k}-${slug(r.jobber_name)}`}>{v(r)}</TableCell>)}
                </TableRow>))}
            </TableBody>
          </Table>)}
      </CardContent></Card>
    </div>
  );
};

export default JobberScorecard;
